import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Spinner,
} from '@patternfly/react-core';
import { CreditCardIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { useApiFetch } from '../../api';
import { formatPrice } from '../../utils/money';
import type { ApiResponseInvoice } from '../../types/api/ApiResponseInvoice';
import type { ApiResponseWalletDepositShow } from '../../types/api/ApiResponseWallet';
import type { ApiResponsePaymentMethodShow } from '../../types/api/ApiResponsePaymentMethod';
import { WALLET_DEPOSIT_FAILED_STATUSES } from '../../types/models/Wallet';
import { describeCard, formatCardExpiry } from './Billing/paymentDisplay';

// Landing page after a Mollie checkout. Three variants, selected by the query param Mollie brings
// the customer back with (the backend builds the return URL — see the api-types contracts):
//   ?inv={invoiceId}        — an invoice payment  (GET /clients/invoices/{id}, clients.md)
//   ?deposit={depositId}    — a wallet top-up     (GET /clients/wallet/deposits/{id}, wallet.md)
//   ?card={paymentMethodId} — a saved-card setup  (GET /clients/payment-methods/{id}, payment-methods.md)
// It ONLY reads state — it never marks anything paid. The webhook (or the backend's own live
// re-fetch from Mollie on these endpoints) is the settlement authority; this page polls until the
// backend reflects the outcome, then reassures the customer.
type PaymentReturnState = 'confirming' | 'paid' | 'failed' | 'processing' | 'error';

// ~8 attempts, ~2.5s apart ≈ 20s. After the cap without a confirmed outcome we show the calm
// Processing state (not an error) — the payment most likely succeeded and the webhook just hasn't
// reconciled yet.
const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2500;

type VariantKey = 'inv' | 'deposit' | 'card';

/** What one poll told us. `detail` is the success copy captured from the confirming response. */
type Evaluation =
  | { outcome: 'paid'; detail: string }
  | { outcome: 'failed'; detail: string }
  | { outcome: 'pending' }
  // A deterministic JSend error ("Top-up not found", "Saved card not found") — stop and show it.
  | { outcome: 'error'; message: string };

interface Variant {
  endpoint: (id: number) => string;
  evaluate: (body: unknown) => Evaluation;
  /** Title-bar copy. */
  title: string;
  subtitle: string;
  breadcrumb: { to: string; label: string };
  crumb: string;
  /** Success / processing / failed copy. */
  paidTitle: string;
  paidFallback: string;
  processingTitle: string;
  processingBody: string;
  failedTitle: string;
  /** Where the buttons lead. */
  backTo: string;
  backLabel: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const VARIANTS: Record<VariantKey, Variant> = {
  inv: {
    endpoint: (id) => `/clients/invoices/${id}`,
    evaluate: (raw) => {
      const body = raw as ApiResponseInvoice | null;
      if (body?.status === 'success' && body.data?.invoice?.paye === 'oui') {
        return { outcome: 'paid', detail: `Your payment for invoice #${body.data.invoice.numero} has been confirmed.` };
      }
      // An invoice never "fails" from this page's point of view: an abandoned checkout just leaves
      // it outstanding, and a documented error here would only come from a tampered URL — keep
      // the pre-wallet behaviour (retry into the calm Processing state).
      return { outcome: 'pending' };
    },
    title: 'Payment',
    subtitle: 'Confirming the status of your invoice payment.',
    breadcrumb: { to: '/organization/invoices', label: 'Invoices' },
    crumb: 'Payment',
    paidTitle: 'Payment received — thank you!',
    paidFallback: 'Your payment has been confirmed.',
    processingTitle: "We're confirming your payment",
    processingBody: 'This can take a moment — your invoice will update shortly. You can safely leave this page.',
    failedTitle: 'The payment was not completed',
    backTo: '/organization/invoices',
    backLabel: 'Back to invoices',
  },
  deposit: {
    endpoint: (id) => `/clients/wallet/deposits/${id}`,
    evaluate: (raw) => {
      const body = raw as ApiResponseWalletDepositShow | null;
      if (body?.status === 'success' && body.data?.deposit) {
        const { deposit, balance } = body.data;
        if (deposit.credited === true) {
          const balanceText = typeof balance === 'string' ? ` Your new balance is ${formatPrice(balance)}.` : '';
          return {
            outcome: 'paid',
            detail: `${formatPrice(deposit.amount)} has been added to your prepaid balance.${balanceText}`,
          };
        }
        if (WALLET_DEPOSIT_FAILED_STATUSES.includes(deposit.status)) {
          return {
            outcome: 'failed',
            detail: `The payment was ${deposit.status}: nothing was taken and your balance was not credited. You can start a new top-up from your billing page.`,
          };
        }
        return { outcome: 'pending' };
      }
      if (body?.status === 'error' && typeof body.message === 'string') {
        return { outcome: 'error', message: body.message };
      }
      return { outcome: 'pending' };
    },
    title: 'Top-up',
    subtitle: 'Confirming your prepaid balance top-up.',
    breadcrumb: { to: '/organization/billing', label: 'Billing' },
    crumb: 'Top-up',
    paidTitle: 'Your balance has been credited',
    paidFallback: 'Your top-up has been credited to your prepaid balance.',
    processingTitle: "We're confirming your top-up",
    processingBody:
      'This can take a moment — your balance will be credited automatically once the payment provider confirms it. You can safely leave this page.',
    failedTitle: 'The top-up was not completed',
    backTo: '/organization/billing?tab=wallet',
    backLabel: 'Back to billing',
  },
  card: {
    endpoint: (id) => `/clients/payment-methods/${id}`,
    evaluate: (raw) => {
      const body = raw as ApiResponsePaymentMethodShow | null;
      if (body?.status === 'success' && body.data?.payment_method) {
        const card = body.data.payment_method;
        if (card.status === 'valid') {
          return {
            outcome: 'paid',
            detail: `${describeCard(card)} (expires ${formatCardExpiry(card)}) is saved. You can now pay invoices, top-ups and domain orders in one click.`,
          };
        }
        if (card.status === 'failed') {
          return {
            outcome: 'failed',
            detail:
              'The card verification did not go through, so the card was not saved. Nothing was charged. You can try again from your billing page.',
          };
        }
        return { outcome: 'pending' };
      }
      if (body?.status === 'error' && typeof body.message === 'string') {
        return { outcome: 'error', message: body.message };
      }
      return { outcome: 'pending' };
    },
    title: 'Saved card',
    subtitle: 'Confirming your card registration.',
    breadcrumb: { to: '/organization/billing', label: 'Billing' },
    crumb: 'Saved card',
    paidTitle: 'Card saved',
    paidFallback: 'Your card has been saved for one-click payments.',
    processingTitle: "We're confirming your card",
    processingBody:
      'This can take a moment — the card will appear in your saved cards once the payment provider confirms it. You can safely leave this page.',
    failedTitle: 'The card could not be saved',
    backTo: '/organization/billing?tab=wallet',
    backLabel: 'Back to billing',
  },
};

// Parse an id param to a positive integer; anything else (non-numeric, zero, negative, fractional)
// is invalid — we have nothing to look up.
function parsePositiveInt(raw: string | null): number | null {
  if (raw === null) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function PaymentReturn() {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Keep the latest apiFetch in a ref. Its identity changes on every OIDC silent token renew
  // (Keycloak access tokens are short-lived, renewed every few minutes — see useApiFetch, keyed on
  // [token, impersonatedClientID]). The polling effect reads it from this ref instead of taking
  // apiFetch as a dependency, so a background token refresh never re-runs the effect and clobbers a
  // terminal paid/processing state back to "confirming" — while in-flight polls still use a current
  // token.
  const apiFetchRef = useRef(apiFetch);
  useEffect(() => {
    apiFetchRef.current = apiFetch;
  }, [apiFetch]);

  // Which variant, and its id. The first recognised param wins; a missing/invalid id is the one
  // hard-error case.
  const { variantKey, id } = useMemo<{ variantKey: VariantKey; id: number | null }>(() => {
    for (const key of ['inv', 'deposit', 'card'] as VariantKey[]) {
      const raw = searchParams.get(key);
      if (raw !== null) {
        return { variantKey: key, id: parsePositiveInt(raw) };
      }
    }
    return { variantKey: 'inv', id: null };
  }, [searchParams]);
  const variant = VARIANTS[variantKey];

  const [status, setStatus] = useState<PaymentReturnState>('confirming');
  // Copy captured from the terminal response (success detail, failure reason, or error message).
  const [detail, setDetail] = useState<string | null>(null);
  // Bumping this re-runs the polling effect — used by the Processing state's "Check again" button.
  const [restartCount, setRestartCount] = useState(0);

  useEffect(() => {
    if (id === null) {
      setStatus('error');
      setDetail(null);
      return;
    }

    setStatus('confirming');
    setDetail(null);

    let active = true;
    let attempts = 0;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();

    const poll = async () => {
      attempts += 1;
      let terminal = false;

      try {
        const response = await apiFetchRef.current(variant.endpoint(id), { signal: controller.signal });
        // Branch on the JSend status, never response.ok — the backend maps controller exceptions to
        // status: 'error' at HTTP 200.
        const body: unknown = await response.json().catch(() => null);
        const evaluation = variant.evaluate(isRecord(body) ? body : null);

        if (evaluation.outcome === 'paid' || evaluation.outcome === 'failed') {
          terminal = true;
          if (active) {
            setDetail(evaluation.detail);
            setStatus(evaluation.outcome);
          }
          return;
        }
        if (evaluation.outcome === 'error') {
          // Deterministic ("Top-up not found" / "Saved card not found" — a tampered or stale URL):
          // surface it instead of optimistically retrying into "processing".
          terminal = true;
          if (active) {
            setDetail(evaluation.message);
            setStatus('error');
          }
          return;
        }
      } catch {
        // Never surface a scary error to someone who just paid: an AbortError (unmount) or a
        // transient network/JSend failure is treated as a non-confirming attempt, then falls
        // through to retry — or, once the cap is hit, to the calm Processing state.
      }

      if (!active || terminal) {
        return;
      }

      if (attempts >= MAX_ATTEMPTS) {
        setStatus('processing');
        return;
      }

      timeoutId = setTimeout(() => {
        void poll();
      }, POLL_INTERVAL_MS);
    };

    void poll();

    return () => {
      active = false;
      controller.abort();
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
    // Deliberately NOT depending on apiFetch — see apiFetchRef above. The effect re-runs only on a
    // new id/variant or an explicit "Check again" (restartCount), never on a background token renew.
  }, [id, variant, restartCount]);

  const goBack = useCallback(() => {
    navigate(variant.backTo);
  }, [navigate, variant.backTo]);

  const handleCheckAgain = useCallback(() => {
    setDetail(null);
    setRestartCount((count) => count + 1);
  }, []);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const backButton = (
    <Button variant="primary" onClick={goBack}>
      {variant.backLabel}
    </Button>
  );

  const renderState = () => {
    switch (status) {
      case 'paid':
        return (
          <EmptyState status="success" titleText={variant.paidTitle} headingLevel="h2" variant="lg">
            <EmptyStateBody>{detail ?? variant.paidFallback}</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>{backButton}</EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        );
      case 'failed':
        return (
          <EmptyState status="danger" titleText={variant.failedTitle} headingLevel="h2" variant="lg">
            <EmptyStateBody>{detail}</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>{backButton}</EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        );
      case 'processing':
        return (
          <EmptyState status="info" titleText={variant.processingTitle} headingLevel="h2" variant="lg">
            <EmptyStateBody>{variant.processingBody}</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>{backButton}</EmptyStateActions>
              <EmptyStateActions>
                <Button variant="link" onClick={handleCheckAgain}>
                  Check again
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        );
      case 'error':
        return (
          <EmptyState status="danger" titleText="Something went wrong" headingLevel="h2" variant="lg">
            <EmptyStateBody>{detail ?? 'We were unable to identify that payment.'}</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>{backButton}</EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        );
      case 'confirming':
      default:
        return (
          <EmptyState
            titleText={`Confirming your ${variant.title.toLowerCase()}…`}
            headingLevel="h2"
            variant="lg"
            icon={Spinner}
          >
            <EmptyStateBody>This only takes a moment.</EmptyStateBody>
          </EmptyState>
        );
    }
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="PaymentReturnBreadcrumb">
          <BreadcrumbItem to="/organization">Organization</BreadcrumbItem>
          <BreadcrumbItem to={variant.breadcrumb.to}>{variant.breadcrumb.label}</BreadcrumbItem>
          <BreadcrumbItem isActive>{variant.crumb}</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <CreditCardIcon />
          </Icon>
          {variant.title}
        </Content>
        <Content component="p">{variant.subtitle}</Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderState()}</PageSection>
    </>
  );
}

export default PaymentReturn;
